import { actorInProject, areasFit, isIanaTimeZone, isTimeOfDay, MAX_ROUTINE_INSTRUCTIONS_CHARS, type ActorDTO, type RoutineReadScope } from "@roomate/core";

/** Form state as typed; the budget stays a string until it is validated. */
export type RoutineFormValues = {
  name: string;
  agentId: string;
  instructions: string;
  reads: RoutineReadScope[];
  timeOfDay: string;
  timezone: string;
  maxBudgetUsd: string;
};

export type RoutineFormErrors = Partial<Record<"name" | "agentId" | "instructions" | "reads" | "timeOfDay" | "timezone" | "maxBudgetUsd", string>>;

export const DEFAULT_TIMEZONE = "Asia/Jerusalem";
export const DEFAULT_BUDGET_USD = "1.00";
export const MAX_BUDGET_USD = 500;
export const MAX_NAME_CHARS = 200;

export const READ_SCOPE_OPTIONS: { scope: RoutineReadScope; label: string; hint: string }[] = [
  { scope: "roomate:conversations", label: "Conversations", hint: "Message threads in this project" },
  { scope: "roomate:activity", label: "Activity", hint: "Task stories and decisions in this project" },
  { scope: "roomate:tasks", label: "Tasks", hint: "Task titles, stages and evidence summaries" },
];

/** docs/specs/routines.md §8.2, said plainly wherever a read scope is chosen. */
export function providerNotice(reads: readonly RoutineReadScope[]): string {
  const chosen = READ_SCOPE_OPTIONS.filter((o) => reads.includes(o.scope)).map((o) => o.label.toLowerCase());
  const list = chosen.length > 1 ? `${chosen.slice(0, -1).join(", ")} and ${chosen[chosen.length - 1]}` : (chosen[0] ?? "project data");
  return `This routine's agent will read project ${list} and send that data to the model provider, as any run does.`;
}

export const emptyRoutineForm = (): RoutineFormValues => ({
  name: "",
  agentId: "",
  instructions: "",
  reads: [],
  timeOfDay: "00:00",
  timezone: DEFAULT_TIMEZONE,
  maxBudgetUsd: DEFAULT_BUDGET_USD,
});

/** Agents that may run this routine: active agents on the project whose allowed areas fit the chosen reads (§5.1). Removed or archived agents are left out. */
export function agentsForRoutine(actors: readonly ActorDTO[] | undefined, projectId: string, reads: readonly string[]): ActorDTO[] {
  return (actors ?? []).filter(
    (a) => a.kind === "agent" && a.active && !a.archived && actorInProject(a, projectId) && areasFit(a.allowedAreas, reads),
  );
}

/** Specific, per-field messages, shown inline (§3.3). Empty means the form may be sent. */
export function validateRoutineForm(v: RoutineFormValues, fitting: readonly Pick<ActorDTO, "id">[]): RoutineFormErrors {
  const e: RoutineFormErrors = {};
  const name = v.name.trim();
  if (!name) e.name = "Give the routine a name.";
  else if (name.length > MAX_NAME_CHARS) e.name = `The name can be at most ${MAX_NAME_CHARS} characters; this one is ${name.length}.`;
  if (v.reads.length === 0) e.reads = "Add at least one read scope";
  if (!v.agentId) e.agentId = fitting.length === 0 && v.reads.length > 0 ? "No agent on this project can read all of these scopes. Remove a scope or widen an agent's areas." : "Choose an agent.";
  else if (!fitting.some((a) => a.id === v.agentId)) e.agentId = "This agent cannot read the chosen scopes. Choose another agent.";
  if (v.instructions.length > MAX_ROUTINE_INSTRUCTIONS_CHARS)
    e.instructions = `Instructions can be at most ${MAX_ROUTINE_INSTRUCTIONS_CHARS.toLocaleString("en")} characters; these are ${v.instructions.length.toLocaleString("en")}.`;
  if (!isTimeOfDay(v.timeOfDay)) e.timeOfDay = "Enter a time of day like 00:00 or 18:30 (24-hour).";
  if (!isIanaTimeZone(v.timezone.trim())) e.timezone = "Enter a time zone name like Asia/Jerusalem or America/New_York.";
  const budget = parseBudget(v.maxBudgetUsd);
  if (budget === null) e.maxBudgetUsd = "Enter the most one run may cost in dollars, for example 1.00.";
  else if (budget <= 0) e.maxBudgetUsd = "The budget cap must be more than $0.";
  else if (budget > MAX_BUDGET_USD) e.maxBudgetUsd = `The budget cap can be at most $${MAX_BUDGET_USD}.`;
  return e;
}

export function parseBudget(raw: string): number | null {
  const s = raw.trim();
  if (!/^\d+(\.\d+)?$/.test(s)) return null;
  return Number(s);
}

/** The body of POST /routines and PATCH /routines/:id. */
export function routineBody(v: RoutineFormValues) {
  return {
    agentId: v.agentId,
    name: v.name.trim(),
    instructions: v.instructions,
    reads: v.reads,
    timeOfDay: v.timeOfDay,
    timezone: v.timezone.trim(),
    maxBudgetUsd: parseBudget(v.maxBudgetUsd) ?? 0,
  };
}
